<template>
  <BaseModal card-width="800px" no-padding no-toolbar no-exit @close="$emit('close')">
    <template #default>
      <div class="share-container bg-background-normal p-1">
        <!-- EXISTING SHARE LINKS (left-col) -->
        <div class="w-80 bg-background-normal p-3 rounded-l-xl flex flex-col justify-between">
          <div class="p-1.5 flex items-center gap-1.5">
            <ExternalShareIcon />
            <BaseText type="label" size="sm">
              Shares
            </BaseText>
          </div>
          <!-- Links List -->
          <div v-if="pastReportShares.length > 0" class="share-list flex flex-col gap-2 p-1 scroll-area fade-in">
            <!-- Share Link Item -->
            <div v-for="pastShare in pastReportShares" :key="pastShare.id"
            class="flex items-center gap-2 py-1.5 pl-1.5 pr-2 hover:bg-background-hover cursor-pointer w-full rounded-md"
            :class="currentViewableShare?.id === pastShare?.id ? 'base-btn' : ''"
            @click="setShareView(modalViews.OLD_SHARE, pastShare)">
              <StaticReportIcon v-if="pastShare.type === 'static'" class="text-icon-normal" />
              <UpdatingReportIcon v-else class="text-icon-normal" />
              <div class="flex flex-1 items-center gap-1.5">
                <BaseText type="label" size="sm" class="text-text-muted flex">
                  {{ formatDate(pastShare.created_at) }}
                </BaseText>
                <div class="w-1 h-1 rounded-full" :class="pastShare?.is_published ? 'bg-secondary-green-100' : 'bg-icon-disabled'" />
              </div>
              <LockedIcon v-if="pastShare.is_password_locked" class="text-icon-normal" />
              <div class="flex items-center gap-1">
                <PreviewIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-normal">
                  {{ pastShare?.view_count }}
                </BaseText>
              </div>
            </div>
          </div>
          <!-- Skeleton Loader -->
          <div v-else-if="loading.fetchAllShares" class="w-full h-full flex flex-col items-center gap-2 share-list p-1">
            <div v-for="i in 3" :key="i" class="skeleton w-full h-8 rounded-md muted border border-background-hover" />
          </div>
          <div v-else class="text-center my-4 fade-in">
            <BaseText type="label" size="sm" class="text-text-subdued">
              No past share links
            </BaseText>
          </div>
          <!-- Switch to New Share Link View (if viewing an existing share link) -->
          <div class=" flex items-center gap-2 text-text-muted p-1.5 hover:bg-background-hover cursor-pointer w-full rounded-md transition-colors"
          @click="setShareView(modalViews.NEW_SHARE, null)">
            <PlusIcon />
            <BaseText type="label" size="sm">
              New Share Link
            </BaseText>
          </div>
        </div>
        <!-- SHARE DETAILS (right-col) -->
        <div class="share-form scroll-area">
          <ShareReportForm
            v-if="view === modalViews.NEW_SHARE"
            :share-form="shareForm"
            :enable-form="enableForm"
            :loading="loading"
            @formChange="updateForm"
            @configChange="updateConfig"
            @createShare="handleGenerateShare"
          />
          <EditShareReport
            v-else
            :current-share="currentViewableShare"
            :loading="loading"
            @update="handleShareUpdate"
            @delete="handleShareDelete"
          />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import LensAPI from '@/api/lens'
import FirebaseAPI from '@/api/firebase'
import _ from 'lodash'
import { providerKeyMap } from '@/utils/lens/dataSourceUtils'
import { mapGetters } from 'vuex'

// Components
import ShareReportForm from './ShareReportForm.vue'
import EditShareReport from './EditShareReport.vue'

// Icons
import ExternalShareIcon from '../../../globals/Icons/ExternalShareIcon.vue'
import StaticReportIcon from '../../../globals/Icons/LensIcons/report/StaticReportIcon.vue'
import UpdatingReportIcon from '../../../globals/Icons/LensIcons/report/UpdatingReportIcon.vue'
import LockedIcon from '../../../globals/Icons/LockedIcon.vue'
import PreviewIcon from '../../../globals/Icons/PreviewIcon.vue'
import PlusIcon from '../../../globals/Icons/PlusIcon.vue'

export default {
  name: 'ShareReportModal',
  components: {
    ShareReportForm,
    EditShareReport,
    ExternalShareIcon,
    StaticReportIcon,
    UpdatingReportIcon,
    LockedIcon,
    PreviewIcon,
    PlusIcon
  },
  props: {
    reportType: {
      type: String,
      default: () => ''
    },
    fetchPayload: {
      type: Object,
      default: () => {}
    },
    currentGraph: {
      type: String,
      default: () => ''
    },
    report: {
      type: Object,
      default: () => {}
    },
    lensId: {
      type: [Number, String],
      default: () => ''
    },
    lensCurrency: {
      type: String,
      default: () => 'USD'
    },
    reportId: {
      type: String,
      default: () => ''
    },
    summaryStats: {
      type: Object,
      default: () => {}
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    currentDateRange: {
      type: Object,
      default: () => {}
    },
    timeseries: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    const modalViews = {
      NEW_SHARE: 'newShare',
      OLD_SHARE: 'oldShare'
    }
    return {
      modalViews,
      view: modalViews.NEW_SHARE,

      fallbackAvatar: require('../../../../assets/images/defaultProfileImage.webp'),

      currentViewableShare: null,

      pastReportShares: [],

      loading: {
        createShare: false,
        delete: false,
        edit: false,
        fetchAllShares: false
      },

      shareForm: {
        name: this.report.name,
        description: this.report.description,
        password: '',
        // TODO @Sam: leave as editable for now, but probably will remove later
        dateRange: { ...this.currentDateRange },
        rollingDateRange: { id: 'last_7_days', name: 'Last 7 days' },
        type: 'static'
      },

      enableForm: {
        password: false,
        trackIps: false,
        showComments: false,
        isPublished: false
      }
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricLookup'])
  },
  async mounted () {
    this.fetchPastShares()
  },
  methods: {
    async fetchPastShares () {
      try {
        this.loading.fetchAllShares = true
        const { data } = await LensAPI.LensReport.getAllShares(this.reportId)
        this.pastReportShares = data
      } catch (e) {
        console.error(e)
        this.$showAlert({ type: 'error', message: 'Error fetching past share reports' })
      } finally {
        this.loading.fetchAllShares = false
      }
    },
    async handleShareUpdate (id, toUpdate) {
      this.loading.edit = true
      try {
        const updatedShare = await LensAPI.LensReport.updateShare(id, toUpdate)
        this.currentViewableShare = {
          ...updatedShare,
          created_by: this.currentViewableShare.created_by
        }
        this.pastReportShares = this.pastReportShares.map((pastShare) => pastShare.id === id ? updatedShare : pastShare)
        this.$showAlert({ type: 'success', message: 'Share link updated' })
      } catch (e) {
        this.$showAlert({ type: 'error', message: 'Error updating share link' })
      } finally {
        this.loading.edit = false
      }
    },
    async handleShareDelete (id) {
      this.loading.delete = true
      try {
        await LensAPI.LensReport.deleteShare(id)
        this.pastReportShares = this.pastReportShares.filter((pastShare) => pastShare.id !== id)
        this.setShareView(this.modalViews.NEW_SHARE, null)
        this.$showAlert({ type: 'success', message: 'Share link successfully deleted' })
      } catch (e) {
        this.$showAlert({ type: 'error', message: 'Error deleting share link' })
      } finally {
        this.loading.delete = false
      }
    },
    formatDate (timestamp) {
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    async setShareView (view, shareData) {
      // Share data is null on new share view
      if (shareData) {
        const createdBy = await this.getShareCreatedBy(shareData.created_by)
        this.currentViewableShare = {
          ...shareData,
          created_by: {
            avatar: createdBy?.avatar ?? this.fallbackAvatar,
            name: createdBy?.name
              ?? (createdBy?.first_name ? `${createdBy?.first_name} ${createdBy?.last_name}` : (createdBy?.email || 'A User'))
          }
        }
        this.enableForm = {
          password: !!shareData?.access_password,
          trackIps: shareData?.is_tracking,
          showComments: shareData?.is_comments_visible,
          isPublished: shareData?.is_published
        }
      } else {
        this.currentViewableShare = null
        this.shareForm.password = ''
        this.enableForm = {
          password: false,
          trackIps: false,
          showComments: false,
          isPublished: false
        }
      }
      this.view = view
    },
    // Query for user data on demand - memoize to prevent requeries
    getShareCreatedBy: _.memoize(async function (uid) {
      return await FirebaseAPI.Users.getPublicById(uid)
    }),
    async handleGenerateShare () {
      this.loading.createShare = true

      const isStatic = this.shareForm.type === 'static'
      // Create copy so we dont mutate original report payload
      const originalPayload = { ...this.fetchPayload }

      // If rolling report, remove any set date range filters & update with the rolling date
      if (!isStatic) {
        originalPayload.filters = originalPayload.filters.filter(filter => filter?.[0]?.key !== 'event_date')
        const rollingDateFilter = [
          [{ key: 'event_date', operation: 'greater_than', value: this.shareForm.rollingDateRange.id }],
          [{ key: 'event_date', operation: 'less_than', value: 'today' }]
        ]
        originalPayload.filters = [...originalPayload.filters, ...rollingDateFilter]
        // For rolling reports, we perform leaderboard style queries (ignore row selection)
        const totalRowsSelected = this.selectedRows.length
        originalPayload.limit = totalRowsSelected
      }

      originalPayload.limit = this.selectedRows.length
      // Reset page to 1 for new share (we add group_by filters so even if its on a sep page we dont care)
      originalPayload.page = 1

      const formattedFetchPayload = LensAPI.AdGroups.utils.createGroupPayload(originalPayload, this.reportType === 'comparative')
      // For static reports, we only want the rows we selected
      const groupFilter = isStatic
        ? [this.selectedRows.map(row => ({
          key: 'group_by',
          value: row.group_by,
          operation: 'equals'
        }))]
        : []

      const fetchQuery = {
        ...formattedFetchPayload,
        query: [...(formattedFetchPayload.query || []), ...(groupFilter || [])]
      }
      // Format metricSnapshot to include only the minimal fields needed for each key
      const minimalMetricSnapshot = {}
      for (const [key, value] of Object.entries(this.getMetricLookup)) {
        minimalMetricSnapshot[key] = {
          id: value.id,
          key: value.key,
          name: value.name,
          type: value.type,
          metric_direction: value.metric_direction
        }
      }
      const metricSnapshot = minimalMetricSnapshot
      const sharePayload = {
        name: this.shareForm.name,
        description: this.shareForm.description,
        report_id: this.reportId,
        provider: providerKeyMap[this.getMetricsPlatform],
        lens_id: this.lensId,
        team_id: this.getTeam?.id ?? null,
        type: this.shareForm.type,
        is_comments_visible: this.enableForm.showComments,
        is_tracking: this.enableForm.trackIps,
        is_published: true,
        access_password: this.shareForm.password ?? null,
        is_password_locked: this.enableForm.password,
        // Report/Ad group relevant data
        query: {
          report_type: this.reportType,
          currency: this.lensCurrency,
          group_by: this.report?.group_by,
          // table_columns: this.report?.table_columns,
          selected_columns: this.report?.selected_columns[this.report?.graph_type],
          metricLookupSnapshot: metricSnapshot,
          // ...(this.report.graph_type === 'line' && { timeseries_snapshot: this.timeseries }),
          table_config: this.report?.table_config,
          graph_type: this.report?.graph_type,

          // Data payload used for a share report to fetch the data (pre formatted for the backend)
          fetch_payload: fetchQuery,
          ...(isStatic ? {
            static_date_range: this.shareForm.dateRange,
          } : {
            rolling_date_range: this.shareForm.rollingDateRange.id
          })

        }
        // Created_by is generated on backend
      }
      try {
        const { data: sharedReport } = await LensAPI.LensReport.createShare(sharePayload)
        this.fetchPastShares()
        console.log('%c GENERATED SHARE REPORT', 'color: orange; font-size: 18px;')
        this.setShareView(this.modalViews.OLD_SHARE, sharedReport)
      } catch (error) {
        console.error(error)
      } finally {
        this.loading.createShare = false
      }
    },
    updateForm (change) {
      console.log('chage', change)
      const { type, value } = change
      this.shareForm[type] = value
    },
    updateConfig (change) {
      const { type, value } = change
      this.enableForm[type] = value
    }
  }
}
</script>

<style scoped>
.share-container{
  border-radius: 20px;
  display: grid;
  width: 100%;
  grid-template-columns: 320px 1fr;
}
.share-list{
  height: 445px;
  overflow-y:auto;
  padding-right: 4px;
}
.scroll-area{
  scroll-margin-right: 4px;
}
.scroll-area::-webkit-scrollbar {
  width: 6px;
}
.scroll-area::-webkit-scrollbar-thumb {
  background-color: #dfe1e7;
  border-radius: 18px;

}
.scroll-area::-webkit-scrollbar-thumb:hover {
  background-color: #bcbec2
}

.share-form{
  height: 565px;
  overflow-y:auto;
  width: 100%;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.08), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.12), 0px 0px 0.75px 0.75px rgba(6, 7, 16, 0.04);
  box-shadow: 0px 3px 3px -1.5px color(display-p3 0.0235 0.0275 0.0588 / 0.08), 0px 1.5px 1.5px -0.75px color(display-p3 0.0235 0.0275 0.0588 / 0.12), 0px 0px 0.75px 0.75px color(display-p3 0.0235 0.0275 0.0588 / 0.04);
}
.submit-btn:disabled{
  box-shadow:none;
  background-color:#F6F8FA;
  color: #A4acb9;
  cursor: not-allowed
}
.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

.invalid-dropdown{
  box-shadow: none;
  border: 1px solid #FF002F;
}
.form-input.invalid {
  box-shadow: none;
  border: 1px solid #FF002F;
}

.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}

/* Section transitions */
.expand-enter-active,
.expand-leave-active {
  transition-property: opacity, height;
  transition-duration: 200ms;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 100ms ease-in;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}
</style>
